
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Contact Negotrix - Free Commercial Review | Commercial Confidence';
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.company) {
      setSubmitStatus('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('');
    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([{ name: formData.name, email: formData.email, company: formData.company, phone: formData.phone, message: formData.message, source: 'contact_page' }]);
      if (!error) {
        setSubmitStatus('Review request sent successfully! We will contact you within 24 hours.');
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      } else {
        setSubmitStatus('Failed to send. Please try again.');
      }
    } catch {
      setSubmitStatus('Failed to send. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-cipher-black text-white">
      <Header />

      {/* Hero */}
      <section className="pt-40 pb-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6">Free Consultation</h2>
          <h1 className="text-[9vw] leading-[0.9] font-black tracking-tighter text-white mb-12">
             Uncover<br /><span className="text-cipher-hypergreen">Your Commercial Outcomes.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl">
            <span className="text-white font-semibold">No upfront fees. No risk.</span> Let's explore your commercial opportunities in a quick 20-minute conversation.
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-24 bg-cipher-dark border-t border-gray-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-16">How to Reach Us</h2>

          <div className="space-y-0 border-t border-gray-800">
            <a href="tel:9540099199" className="group flex items-center justify-between py-10 border-b border-gray-800 hover:bg-cipher-dark transition-colors px-6 -mx-6">
              <div>
                <div className="text-3xl lg:text-5xl font-bold group-hover:text-cipher-hypergreen transition-colors">Quick Call</div>
                 <div className="text-gray-400 text-lg mt-2">Speak directly with our commercial negotiation expert</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-cipher-hypergreen">+91 9540099199</div>
                <div className="text-gray-500 text-sm mt-1">Available Mon–Sat, 9AM–7PM IST</div>
              </div>
            </a>

            <a href="mailto:amit@negotrix.com" className="group flex items-center justify-between py-10 border-b border-gray-800 hover:bg-cipher-dark transition-colors px-6 -mx-6">
              <div>
                <div className="text-3xl lg:text-5xl font-bold group-hover:text-cipher-hypergreen transition-colors">Email Direct</div>
                <div className="text-gray-400 text-lg mt-2">Send your questions to our founder</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-cipher-hypergreen">amit@negotrix.com</div>
                <div className="text-gray-500 text-sm mt-1">Response within 24 hours</div>
              </div>
            </a>

            <a href="https://calendly.com/amit-negotrix/30min" target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between py-10 border-b border-gray-800 hover:bg-cipher-dark transition-colors px-6 -mx-6">
              <div>
                <div className="text-3xl lg:text-5xl font-bold group-hover:text-cipher-hypergreen transition-colors">Schedule Meeting</div>
                <div className="text-gray-400 text-lg mt-2">Book a 20-minute discovery call on Calendly</div>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-6 py-3 text-sm tracking-wide font-bold uppercase rounded-full text-cipher-black bg-cipher-hypergreen group-hover:bg-white transition-all duration-300">
                  Book Now
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Commercial Review Form */}
      <section className="py-24 bg-cipher-black border-t border-gray-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
               <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6">Start Commercial Review</h2>
              <h3 className="text-5xl lg:text-6xl font-black tracking-tight mb-8">             Start Your<br /><span className="text-cipher-hypergreen">Commercial Review.</span></h3>
              <p className="text-gray-400 text-xl mb-12">Tell us about your business and we'll identify commercial opportunities across your entire vendor portfolio.</p>

              <div className="space-y-6">
                {[
                  { icon: 'ri-shield-check-fill', label: 'No Upfront Fees', desc: 'Zero cost to get started. We invest our time upfront.' },
                  { icon: 'ri-hand-coin-fill', label: 'Pay on Results', desc: 'We earn a percentage only of verified outcomes we deliver.' },
                  { icon: 'ri-time-fill', label: 'Fast Turnaround', desc: '14-day comprehensive analysis with full commercial report.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-cipher-hypergreen rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className={`${item.icon} text-cipher-black text-lg`}></i>
                    </div>
                    <div>
                      <div className="font-bold text-white text-lg">{item.label}</div>
                      <div className="text-gray-400">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-cipher-dark border border-gray-800 rounded-[2rem] p-10 lg:p-12">
              <form onSubmit={handleSubmit} className="space-y-6" name="contact-assessment" data-netlify="true">
                <input type="hidden" name="form-name" value="contact-assessment" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm tracking-widest uppercase text-gray-500 font-bold mb-3" htmlFor="contact-name">Full Name *</label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-cipher-black border border-gray-800 text-white rounded-xl px-5 py-4 focus:outline-none focus:border-cipher-hypergreen transition-colors placeholder-gray-600"
                      placeholder="Amit Kumar"
                    />
                  </div>
                  <div>
                    <label className="block text-sm tracking-widest uppercase text-gray-500 font-bold mb-3" htmlFor="contact-company">Company *</label>
                    <input
                      id="contact-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-cipher-black border border-gray-800 text-white rounded-xl px-5 py-4 focus:outline-none focus:border-cipher-hypergreen transition-colors placeholder-gray-600"
                      placeholder="Acme Corp"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm tracking-widest uppercase text-gray-500 font-bold mb-3" htmlFor="contact-email">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-cipher-black border border-gray-800 text-white rounded-xl px-5 py-4 focus:outline-none focus:border-cipher-hypergreen transition-colors placeholder-gray-600"
                    placeholder="amit@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm tracking-widest uppercase text-gray-500 font-bold mb-3" htmlFor="contact-phone">Phone</label>
                  <input
                    id="contact-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-cipher-black border border-gray-800 text-white rounded-xl px-5 py-4 focus:outline-none focus:border-cipher-hypergreen transition-colors placeholder-gray-600"
                    placeholder="+91 9540099199"
                  />
                </div>

                <div>
                  <label className="block text-sm tracking-widest uppercase text-gray-500 font-bold mb-3" htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full bg-cipher-black border border-gray-800 text-white rounded-xl px-5 py-4 focus:outline-none focus:border-cipher-hypergreen transition-colors placeholder-gray-600 resize-none"
                    placeholder="Tell us about your business and cost challenges..."
                  />
                  <div className="text-right text-gray-600 text-sm mt-1">{formData.message.length}/500</div>
                </div>

                {submitStatus && (
                  <div className={`p-4 rounded-xl text-sm font-medium ${submitStatus.includes('success') ? 'bg-cipher-hypergreen/20 text-cipher-hypergreen border border-cipher-hypergreen/30' : 'bg-red-900/20 text-red-400 border border-red-900/30'}`}>
                    {submitStatus}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 text-sm tracking-wide font-bold uppercase rounded-full text-cipher-black bg-cipher-hypergreen hover:bg-white transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Start Your Commercial Review'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Big CTA */}
      <section className="py-32 lg:py-48 bg-cipher-dark border-t border-gray-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-[8vw] leading-[0.9] font-black tracking-tighter text-white mb-6">
            No upfront fees.<br /><span className="text-cipher-hypergreen">No risk.</span>
          </h2>
        </div>
      </section>

      <Footer />
    </div>
  );
}
